import React, { useState } from 'react';
import { 
  FileSpreadsheet, 
  Sparkles, 
  RotateCcw, 
  Eye, 
  X, 
  Info, 
  Sliders,
  FileText,
  Hash
} from 'lucide-react';
import { DelimiterCandidate } from '../../utils/csvParser';

export interface FormatMeta {
  delimiter: string;
  delimiterName: string;
  headerRowIndex: number;
  hasHeader: boolean;
  confidence: 'high' | 'medium' | 'low';
  skippedPreambleCount: number;
  headerDetectionReason?: string;
  delimiterCandidates?: DelimiterCandidate[];
  rawSampleLines?: string[];
}

interface CsvFormatControlsProps {
  formatMeta: FormatMeta;
  selectedDelimiter: string; // 'auto' or ',', '\t', ';', '|', ':', etc.
  selectedHeaderRow: number; // -99 for auto, -1 for none, 0 for row 1, 1 for row 2...
  hasHeaderOverride?: boolean; // optional explicit toggle
  onFormatChange: (options: { delimiter?: string; headerRowIndex?: number; hasHeader?: boolean }) => void;
  fileName?: string | null;
  totalRecordsCount?: number;
  totalColumnsCount?: number;
}

const COMMON_DELIMITERS = [
  { value: ',', label: 'Comma (,)', char: ',' },
  { value: '\t', label: 'Tab (\\t - TSV)', char: '\\t' },
  { value: ';', label: 'Semicolon (;)', char: ';' },
  { value: '|', label: 'Pipe (|)', char: '|' },
  { value: ':', label: 'Colon (:)', char: ':' }
];

export const CsvFormatControls: React.FC<CsvFormatControlsProps> = ({
  formatMeta,
  selectedDelimiter,
  selectedHeaderRow,
  hasHeaderOverride,
  onFormatChange,
  fileName,
  totalRecordsCount,
  totalColumnsCount
}) => {
  const [showRawInspector, setShowRawInspector] = useState(false);
  const [customDelimiterInput, setCustomDelimiterInput] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  const isCustomActive = !['auto', ',', '\t', ';', '|', ':'].includes(selectedDelimiter);
  const isAutoDelimiter = selectedDelimiter === 'auto';
  const isAutoHeader = selectedHeaderRow === -99;
  const isHeaderless = selectedHeaderRow === -1 || (hasHeaderOverride === false) || (!formatMeta.hasHeader && isAutoHeader);
  const hasManualOverrides = !isAutoDelimiter || !isAutoHeader || hasHeaderOverride !== undefined;

  // Find candidate delimiter that might be an alternative
  const secondaryCandidate = formatMeta.delimiterCandidates?.find(
    c => c.char !== formatMeta.delimiter && c.isProbable && c.count > 0
  );

  const handleResetToAuto = () => {
    setShowCustomInput(false);
    onFormatChange({
      delimiter: 'auto',
      headerRowIndex: -99,
      hasHeader: undefined
    });
  };

  const handleToggleHeaders = () => {
    if (isHeaderless) {
      // Turn headers ON -> default to row 0 or auto
      onFormatChange({
        headerRowIndex: 0,
        hasHeader: true
      });
    } else {
      // Turn headers OFF -> -1
      onFormatChange({
        headerRowIndex: -1,
        hasHeader: false
      });
    }
  };

  return (
    <div className="p-4 bg-zinc-900 rounded-md border border-zinc-800/90 shadow-2xs flex flex-col gap-3.5 transition-all">
      {/* Top Bar: Active Status, Detection Badges & Inspector Trigger */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pb-3 border-b border-zinc-800">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 shadow-2xs">
            <FileSpreadsheet className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-sm text-zinc-200">
                {fileName || 'Delimited Spreadsheet'}
              </span>

              {/* Confidence Badge */}
              <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold tracking-wide flex items-center gap-1 ${
                formatMeta.confidence === 'high'
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : formatMeta.confidence === 'medium'
                    ? 'bg-blue-50 text-blue-700 border border-blue-200'
                    : 'bg-amber-50 text-amber-700 border border-amber-200'
              }`}>
                <Sparkles className="w-3 h-3" />
                {isAutoDelimiter ? `Auto-Detected (${formatMeta.confidence} confidence)` : 'Manual Override'}
              </span>

              {formatMeta.skippedPreambleCount > 0 && !isHeaderless && (
                <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-purple-50 text-purple-700 border border-purple-200">
                  Skipped {formatMeta.skippedPreambleCount} preamble row{formatMeta.skippedPreambleCount > 1 ? 's' : ''}
                </span>
              )}

              {isHeaderless && (
                <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-zinc-800 text-zinc-400 border border-zinc-800">
                  No Header Row (Auto-Numbered)
                </span>
              )}
            </div>

            <p className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1 flex-wrap">
              <span>Delimiter: <strong className="text-zinc-300">{formatMeta.delimiterName}</strong></span>
              <span>&bull;</span>
              <span>Header: <strong className="text-zinc-300">{isHeaderless ? 'None' : `Line ${(formatMeta.headerRowIndex >= 0 ? formatMeta.headerRowIndex : 0) + 1}`}</strong></span>
              {totalRecordsCount !== undefined && totalColumnsCount !== undefined && (
                <>
                  <span>&bull;</span>
                  <span><strong>{totalRecordsCount.toLocaleString()}</strong> records, <strong>{totalColumnsCount}</strong> columns</span>
                </>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-end sm:self-auto flex-wrap">
          {hasManualOverrides && (
            <button
              onClick={handleResetToAuto}
              className="px-2.5 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-md text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
              title="Re-apply automatic delimiter and header detection"
            >
              <RotateCcw className="w-3.5 h-3.5 text-zinc-500" />
              <span>Reset to Auto</span>
            </button>
          )}

          <button
            onClick={() => setShowRawInspector(true)}
            className="px-2.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-md text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
            title="Inspect raw CSV file lines and delimiter matrix"
          >
            <Eye className="w-3.5 h-3.5" />
            <span>Raw File &amp; Matrix</span>
          </button>
        </div>
      </div>

      {/* Alternative Candidate Alert Prompt */}
      {secondaryCandidate && isAutoDelimiter && (
        <div className="p-2.5 bg-amber-50/90 border border-amber-200 rounded-md flex items-center justify-between gap-2 text-xs text-amber-900">
          <div className="flex items-center gap-2">
            <Info className="w-4 h-4 text-amber-600 shrink-0" />
            <span>
              We also detected potential <strong>{secondaryCandidate.name}</strong> columns ({secondaryCandidate.count} occurrences).
            </span>
          </div>
          <button
            onClick={() => onFormatChange({ delimiter: secondaryCandidate.char })}
            className="px-2 py-1 bg-amber-200 hover:bg-amber-300 text-amber-950 font-bold rounded-md text-[11px] transition-colors cursor-pointer shrink-0"
          >
            Switch to {(secondaryCandidate.name || 'Delimiter').split(' ')[0]} →
          </button>
        </div>
      )}

      {/* Primary Override Controls Section */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
        {/* Delimiter Selector Column */}
        <div className="md:col-span-7 flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <label className="text-xs font-bold text-zinc-300 uppercase tracking-wider flex items-center gap-1.5">
              <Sliders className="w-3.5 h-3.5 text-blue-600" />
              Delimiter Format
            </label>
            <span className="text-[11px] text-zinc-400">
              Active: <strong className="text-zinc-400">{formatMeta.delimiterName}</strong>
            </span>
          </div>

          <div className="flex items-center gap-1.5 flex-wrap">
            {/* Auto Delimiter Button */}
            <button
              onClick={() => onFormatChange({ delimiter: 'auto' })}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                isAutoDelimiter
                  ? 'bg-[#1473E6] text-white shadow-2xs'
                  : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300'
              }`}
            >
              <Sparkles className="w-3 h-3" />
              Auto ({formatMeta.delimiterName.split(' ')[0]})
            </button>

            {/* Standard Delimiter Buttons */}
            {COMMON_DELIMITERS.map(d => (
              <button
                key={d.value}
                onClick={() => {
                  setShowCustomInput(false);
                  onFormatChange({ delimiter: d.value });
                }}
                className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer flex items-center gap-1 ${
                  selectedDelimiter === d.value
                    ? 'bg-[#1473E6] text-white shadow-2xs'
                    : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300'
                }`}
              >
                <span>{d.label}</span>
              </button>
            ))}

            {/* Custom Delimiter Trigger */}
            <button
              onClick={() => setShowCustomInput(!showCustomInput)}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
                isCustomActive || showCustomInput
                  ? 'bg-[#1473E6] text-white shadow-2xs'
                  : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300'
              }`}
            >
              Custom...
            </button>
          </div>

          {showCustomInput && (
            <div className="flex items-center gap-2 mt-1 p-2 bg-zinc-950 border border-zinc-800 rounded-md">
              <span className="text-xs text-zinc-400 font-medium">Custom Character:</span>
              <input
                type="text"
                maxLength={2}
                value={customDelimiterInput}
                onChange={(e) => setCustomDelimiterInput(e.target.value)}
                placeholder="e.g. ^ or ~"
                className="w-16 px-2 py-1 bg-zinc-900 border border-zinc-700 rounded text-xs font-mono text-center font-bold outline-none focus:ring-2 focus:ring-blue-600"
              />
              <button
                onClick={() => {
                  if (customDelimiterInput) {
                    onFormatChange({ delimiter: customDelimiterInput });
                  }
                }}
                disabled={!customDelimiterInput}
                className="px-3 py-1 bg-[#1473E6] hover:bg-blue-700 disabled:opacity-50 text-white rounded text-xs font-bold cursor-pointer"
              >
                Apply Custom
              </button>
            </div>
          )}
        </div>

        {/* Header Detection & Overrides Column */}
        <div className="md:col-span-5 flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <label className="text-xs font-bold text-zinc-300 uppercase tracking-wider flex items-center gap-1.5">
              <Hash className="w-3.5 h-3.5 text-blue-600" />
              Header Row Setting
            </label>
            
            {/* Header Switch Toggle */}
            <button
              onClick={handleToggleHeaders}
              className="flex items-center gap-1.5 text-xs font-semibold text-zinc-400 hover:text-zinc-100 cursor-pointer"
            >
              <span>First row is header:</span>
              <span className={`px-1.5 py-0.5 rounded font-bold text-[10px] uppercase ${
                !isHeaderless ? 'bg-emerald-100 text-emerald-800' : 'bg-zinc-700 text-zinc-400'
              }`}>
                {!isHeaderless ? 'YES' : 'NO'}
              </span>
            </button>
          </div>

          <div className="flex items-center gap-2">
            <select
              value={selectedHeaderRow}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                onFormatChange({
                  headerRowIndex: val,
                  hasHeader: val !== -1
                });
              }}
              className="flex-1 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700/80 border border-zinc-700 rounded-md text-xs font-bold text-zinc-300 focus:outline-none focus:ring-2 focus:ring-blue-600 cursor-pointer"
            >
              <option value={-99}>Auto-Detect ({formatMeta.hasHeader ? `Row ${formatMeta.headerRowIndex + 1}` : 'No Headers'})</option>
              <option value={0}>Line 1 is Header (Standard)</option>
              <option value={1}>Line 2 is Header (Skip Line 1 Title)</option>
              <option value={2}>Line 3 is Header (Skip 2 Preamble Lines)</option>
              <option value={3}>Line 4 is Header</option>
              <option value={4}>Line 5 is Header</option>
              <option value={-1}>No Header (Auto-number columns: Column 1, 2...)</option>
            </select>
          </div>

          {formatMeta.headerDetectionReason && (
            <p className="text-[11px] text-zinc-400 italic">
              {formatMeta.headerDetectionReason}
            </p>
          )}
        </div>
      </div>

      {/* Raw File & Delimiter Matrix Modal */}
      {showRawInspector && (
        <div className="fixed inset-0 bg-zinc-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-md shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-150 border border-zinc-800 max-h-[85vh]">
            <div className="flex justify-between items-center p-5 border-b border-zinc-800 bg-zinc-950">
              <div className="flex items-center gap-2.5">
                <div className="flex items-center gap-1.5 mr-2">
                  <button 
                    onClick={() => setShowRawInspector(false)} 
                    className="w-3.5 h-3.5 rounded-full bg-red-500 hover:bg-red-600 transition-colors cursor-pointer" 
                    title="Close Window" 
                  ></button>
                  <div className="w-3.5 h-3.5 rounded-full bg-yellow-500/50" />
                  <div className="w-3.5 h-3.5 rounded-full bg-green-500/50" />
                </div>
                <div className="w-8 h-8 rounded-md bg-blue-500/10 text-blue-400 border border-blue-500/20 flex items-center justify-center">
                  <Eye className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-zinc-200">Raw CSV &amp; Delimiter Matrix Inspection</h3>
                  <p className="text-xs text-zinc-500">{fileName || 'Uploaded file'} • Auto-detection scoring breakdown</p>
                </div>
              </div>
            </div>

            <div className="p-6 overflow-y-auto flex flex-col gap-5">
              {/* Candidate Delimiters Score Matrix */}
              <div>
                <h4 className="text-xs font-bold text-zinc-300 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Sliders className="w-3.5 h-3.5 text-blue-600" />
                  Delimiter Analysis &amp; Confidence Ranking
                </h4>
                
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                  {formatMeta.delimiterCandidates?.map((cand) => (
                    <div
                      key={cand.char}
                      onClick={() => {
                        onFormatChange({ delimiter: cand.char });
                        setShowRawInspector(false);
                      }}
                      className={`p-3 rounded-md border transition-all cursor-pointer flex flex-col justify-between ${
                        selectedDelimiter === cand.char || (isAutoDelimiter && formatMeta.delimiter === cand.char)
                          ? 'bg-blue-950/60 border-blue-600 ring-1 ring-blue-500'
                          : 'bg-zinc-950 border-zinc-800 hover:bg-zinc-800 hover:border-zinc-700'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-xs text-zinc-200">{cand.name}</span>
                        {(selectedDelimiter === cand.char || (isAutoDelimiter && formatMeta.delimiter === cand.char)) && (
                          <span className="w-2 h-2 rounded-full bg-[#1473E6]"></span>
                        )}
                      </div>
                      
                      <div className="mt-2 space-y-0.5 text-[11px] text-zinc-500">
                        <div>Occurrences: <strong className="text-zinc-300">{cand.count}</strong></div>
                        <div>Est. Columns: <strong className="text-zinc-300">{cand.avgCols}</strong></div>
                        <div>Line Coverage: <strong className="text-zinc-300">{cand.lineParticipation}%</strong></div>
                      </div>

                      <div className="mt-2 pt-2 border-t border-zinc-800/60 text-[10px] font-bold text-blue-600">
                        Click to select →
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Raw Sample Text Lines */}
              {formatMeta.rawSampleLines && formatMeta.rawSampleLines.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold text-zinc-300 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5 text-blue-600" />
                    First Raw Lines in File
                  </h4>
                  <div className="bg-zinc-900 text-zinc-200 font-mono text-xs p-4 rounded-md overflow-x-auto space-y-1.5 border border-zinc-800">
                    {formatMeta.rawSampleLines.map((line, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <span className="text-zinc-500 select-none text-[11px] w-6 text-right shrink-0">
                          {idx + 1}
                        </span>
                        <span className={idx === formatMeta.headerRowIndex && formatMeta.hasHeader ? 'text-emerald-300 font-bold bg-emerald-950/60 px-1 rounded' : ''}>
                          {line}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-zinc-800 bg-zinc-950 flex items-center justify-between">
              <span className="text-xs text-zinc-500">Click any delimiter above to switch parsing mode immediately.</span>
              <button
                onClick={() => setShowRawInspector(false)}
                className="px-4 py-2 bg-[#1473E6] hover:bg-blue-700 text-white rounded-md text-xs font-bold transition-colors cursor-pointer"
              >
                Close Inspector
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
